import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Share2, Users, Link2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function Referral() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [data, setData] = useState({
    referral_code: "", tenant_slug: "", tenant_name: "",
    reward_per_referral: 500, total_referrals: 0,
    approved_referrals: 0, total_earned: 0, pending_rewards: 0,
    referrals: [],
  });

  useEffect(() => { api.get("/customer/referrals").then((r) => setData(r.data)); }, []);

  const shareUrl = useMemo(() => {
    if (!data.referral_code) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const params = new URLSearchParams();
    if (data.tenant_slug) params.set("tenant", data.tenant_slug);
    params.set("ref", data.referral_code);
    return `${origin}/?${params.toString()}`;
  }, [data.referral_code, data.tenant_slug]);

  const shareText = useMemo(() => {
    const brand = data.tenant_name || "our Saving Wallet app";
    return `Join me on ${brand}! Save daily, earn bonuses on every premium. Use my referral code ${data.referral_code} or tap the link to sign up.`;
  }, [data.tenant_name, data.referral_code]);

  const copy = async (s, label) => {
    try { await navigator.clipboard.writeText(s); toast.success(label || t("code_copied")); }
    catch { toast.error(t("copy_failed")); }
  };

  const share = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: data.tenant_name || t("refer_and_earn"),
          text: shareText,
          url: shareUrl,
        });
      } catch { /* user-cancelled */ }
    } else {
      copy(shareUrl, t("link_copied"));
    }
  };

  return (
    <div data-testid="referral-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">{t("refer_and_earn")}</div>
      </div>

      <div className="px-5 pt-6">
        {/* Hero card with code */}
        <div className="gold-grad rounded-3xl p-6 text-tenant-primary card-shadow-lg text-center">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-80">{t("your_referral_code")}</div>
          <div data-testid="referral-code" className="text-3xl font-extrabold tracking-tight mt-2 select-all">{data.referral_code || "—"}</div>
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            <button
              data-testid="copy-code"
              onClick={() => copy(data.referral_code, t("code_copied"))}
              className="inline-flex items-center gap-1.5 bg-tenant-primary text-white text-xs font-bold px-4 py-2 rounded-full tap-scale"
            >
              <Copy className="w-3.5 h-3.5" /> {t("copy_code")}
            </button>
            <button
              data-testid="share-link"
              onClick={share}
              className="inline-flex items-center gap-1.5 bg-white text-tenant-primary text-xs font-bold px-4 py-2 rounded-full tap-scale shadow-md"
            >
              <Share2 className="w-3.5 h-3.5" /> {t("share_link_btn")}
            </button>
          </div>
        </div>

        {/* Share link box */}
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-3 card-shadow">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
            <Link2 className="w-3 h-3" /> {t("your_invite_link")}
          </div>
          <div className="flex items-center gap-2">
            <div
              data-testid="share-url"
              className="flex-1 text-[12px] font-mono text-slate-700 bg-slate-50 px-2.5 py-2 rounded-lg truncate"
              title={shareUrl}
            >
              {shareUrl || "—"}
            </div>
            <button
              data-testid="copy-link"
              onClick={() => copy(shareUrl, t("link_copied"))}
              className="shrink-0 inline-flex items-center gap-1 bg-tenant-primary text-white text-xs font-bold px-3 py-2 rounded-lg tap-scale"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white border border-slate-100 rounded-2xl p-3 card-shadow text-center">
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-400">{t("total")}</div>
            <div data-testid="total-referrals" className="text-2xl font-extrabold tracking-tight mt-1">{data.total_referrals}</div>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-3 card-shadow text-center">
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-400">{t("earned")}</div>
            <div data-testid="total-referral-earned" className="text-xl font-extrabold tracking-tight mt-1 text-emerald-600">{fmtINR(data.total_earned)}</div>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-3 card-shadow text-center">
            <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-400">{t("pending_label")}</div>
            <div data-testid="pending-rewards" className="text-xl font-extrabold tracking-tight mt-1 text-amber-600">{fmtINR(data.pending_rewards)}</div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-6 bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
          <h3 className="font-bold text-sm mb-2">{t("how_it_works")}</h3>
          <ol className="space-y-2 text-[13px] text-slate-600">
            <li>1. {t("how_step_1")}</li>
            <li>2. {t("how_step_2")}</li>
            <li>3. {t("how_step_3")}</li>
            <li>4. {t("how_step_4_prefix")} <b className="text-emerald-600">{fmtINR(data.reward_per_referral)}</b> {t("how_step_4_suffix")}</li>
          </ol>
        </div>

        {/* Recent referrals */}
        <div className="mt-5">
          <h3 className="font-extrabold tracking-tight mb-3 flex items-center gap-2">
            <Users className="w-4 h-4" /> {t("recent_referrals")}
          </h3>
          <div className="space-y-2" data-testid="referrals-list">
            {data.referrals.map((r) => {
              const status = r.status || "pending";
              const isApproved = status === "approved";
              const isAwaiting = status === "awaiting_approval";
              return (
                <div key={r.id} className="bg-white border border-slate-100 rounded-2xl p-3 flex items-center justify-between card-shadow">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{r.referee_name || "—"}</div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-1">
                      {fmtDate(r.created_at)}
                      {isApproved && (
                        <span className="ml-1 inline-flex items-center gap-1 text-emerald-600 font-bold">
                          <CheckCircle2 className="w-3 h-3" /> {t("paid")}
                        </span>
                      )}
                      {isAwaiting && (
                        <span className="ml-1 inline-flex items-center gap-1 text-amber-600 font-bold">
                          <Clock className="w-3 h-3" /> {t("awaiting_admin")}
                        </span>
                      )}
                      {!isApproved && !isAwaiting && (
                        <span className="ml-1 inline-flex items-center gap-1 text-slate-400 font-bold">
                          <Clock className="w-3 h-3" /> {t("waiting_first_payment")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`font-extrabold ${isApproved ? "text-emerald-600" : isAwaiting ? "text-amber-600" : "text-slate-300"}`}>
                    {isApproved || isAwaiting
                      ? `+${fmtINR(r.reward || data.reward_per_referral)}`
                      : `+${fmtINR(data.reward_per_referral)}`}
                  </div>
                </div>
              );
            })}
            {data.referrals.length === 0 && (
              <div className="text-center text-slate-400 text-sm py-6" data-testid="referrals-empty">
                {t("no_referrals_yet")} {fmtINR(data.reward_per_referral)}!
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}
